import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Shield } from 'lucide-react';
import SEO from '../components/SEO';
import { seoData } from '../data/translations';

const DatenschutzPage = () => {
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <SEO 
        title={seoData.datenschutz.title}
        description={seoData.datenschutz.description}
      />
      <Navbar />
      <main>
        <section className="relative pt-40 pb-20">
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#D4AF37]" />
                <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">Legal</span>
                <div className="h-px w-12 bg-[#D4AF37]" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6">Datenschutzerklärung</h1>
            </div>

            <div className="bg-white rounded-3xl border border-black/5 shadow-xl p-8 md:p-12">
              <div className="prose prose-lg max-w-none">
                
                {/* Section 1: Overview */}
                <div className="mb-10">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-6 h-6 text-[#D4AF37]" />
                    <h2 className="text-2xl font-bold text-black m-0">1. Datenschutz auf einen Blick</h2>
                  </div>
                  <h3 className="text-xl font-semibold text-black mb-3">Allgemeine Hinweise</h3>
                  <p className="text-black/70">
                    Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
                    Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit 
                    denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema 
                    Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
                  </p>
                </div>

                {/* Section 2: Data Collection */}
                <div className="mb-10">
                  <h3 className="text-xl font-semibold text-black mb-3">Datenerfassung auf dieser Website</h3>
                  <h4 className="text-lg font-medium text-black mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h4>
                  <p className="text-black/70 mb-4">
                    Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
                    können Sie dem Abschnitt „Hinweis zur verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
                  </p>
                  <h4 className="text-lg font-medium text-black mb-2">Wie erfassen wir Ihre Daten?</h4>
                  <p className="text-black/70 mb-4">
                    Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich 
                    z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben. Andere Daten werden automatisch 
                    oder nach Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind 
                    vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). 
                    Die Erfassung dieser Daten erfolgt automatisch, sobald Sie diese Website betreten.
                  </p>
                  <h4 className="text-lg font-medium text-black mb-2">Wofür nutzen wir Ihre Daten?</h4>
                  <p className="text-black/70 mb-4">
                    Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
                    Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
                  </p>
                  <h4 className="text-lg font-medium text-black mb-2">Welche Rechte haben Sie bezüglich Ihrer Daten?</h4>
                  <p className="text-black/70">
                    Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer 
                    gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung 
                    oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt 
                    haben, können Sie diese Einwilligung jederzeit für die Zukunft widerrufen. Außerdem haben Sie das 
                    Recht, unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten 
                    zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
                  </p>
                </div>

                {/* Section 3: Hosting */}
                <div className="mb-10">
                  <h2 className="text-2xl font-bold text-black mb-4">2. Hosting</h2>
                  <p className="text-black/70 mb-4">
                    Wir hosten die Inhalte unserer Website bei folgendem Anbieter:
                  </p>
                  <h3 className="text-xl font-semibold text-black mb-3">Externes Hosting</h3>
                  <p className="text-black/70">
                    Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst 
                    werden, werden auf den Servern des Hosters gespeichert. Hierbei kann es sich v. a. um IP-Adressen, 
                    Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, 
                    Websitezugriffe und sonstige Daten, die über eine Website generiert werden, handeln. Das externe 
                    Hosting erfolgt zum Zwecke der Vertragserfüllung gegenüber unseren potenziellen und bestehenden 
                    Kunden (Art. 6 Abs. 1 lit. b DSGVO) und im Interesse einer sicheren, schnellen und effizienten 
                    Bereitstellung unseres Online-Angebots durch einen professionellen Anbieter (Art. 6 Abs. 1 lit. f DSGVO).
                  </p>
                </div>

                {/* Section 4: General Information */}
                <div className="mb-10">
                  <h2 className="text-2xl font-bold text-black mb-4">3. Allgemeine Hinweise und Pflichtinformationen</h2>
                  <h3 className="text-xl font-semibold text-black mb-3">Datenschutz</h3>
                  <p className="text-black/70 mb-4">
                    Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln 
                    Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften 
                    sowie dieser Datenschutzerklärung. Wenn Sie diese Website benutzen, werden verschiedene 
                    personenbezogene Daten erhoben. Personenbezogene Daten sind Daten, mit denen Sie persönlich 
                    identifiziert werden können. Die vorliegende Datenschutzerklärung erläutert, welche Daten wir 
                    erheben und wofür wir sie nutzen. Sie erläutert auch, wie und zu welchem Zweck das geschieht.
                  </p>
                  <p className="text-black/70">
                    Wir weisen darauf hin, dass die Datenübertragung im Internet (z. B. bei der Kommunikation per 
                    E-Mail) Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff 
                    durch Dritte ist nicht möglich.
                  </p>
                </div>

                {/* Section 5: Responsible Party */}
                <div className="mb-10">
                  <h3 className="text-xl font-semibold text-black mb-3">Hinweis zur verantwortlichen Stelle</h3>
                  <p className="text-black/70 mb-4">
                    Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
                  </p>
                  <div className="bg-[#F9F9F9] p-4 rounded-lg mb-4">
                    <p className="font-semibold text-black">Yetkin Dienstleistungen GmbH</p>
                    <p className="text-black/70">Reuchlinstr. 10</p>
                    <p className="text-black/70">10553 Berlin</p>
                    <p className="text-black/70 mt-2">
                      Telefon: <a href="tel:+493033771813" className="text-[#D4AF37] hover:underline">030 33771813</a>
                    </p>
                    <p className="text-black/70">
                      E-Mail: <a href="mailto:info@yetkin-dienstleistungen.de" className="text-[#D4AF37] hover:underline">info@yetkin-dienstleistungen.de</a>
                    </p>
                  </div>
                  <p className="text-black/70">
                    Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam 
                    mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten 
                    (z. B. Namen, E-Mail-Adressen o. Ä.) entscheidet.
                  </p>
                </div>

                {/* Section 6: Storage Duration */}
                <div className="mb-10">
                  <h3 className="text-xl font-semibold text-black mb-3">Speicherdauer</h3>
                  <p className="text-black/70">
                    Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wurde, 
                    verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung 
                    entfällt. Wenn Sie ein berechtigtes Löschersuchen geltend machen oder eine Einwilligung zur 
                    Datenverarbeitung widerrufen, werden Ihre Daten gelöscht, sofern wir keine anderen rechtlich 
                    zulässigen Gründe für die Speicherung Ihrer personenbezogenen Daten haben (z. B. steuer- oder 
                    handelsrechtliche Aufbewahrungsfristen); im letztgenannten Fall erfolgt die Löschung nach 
                    Fortfall dieser Gründe.
                  </p>
                </div>

                {/* Section 7: Legal Basis */}
                <div className="mb-10">
                  <h3 className="text-xl font-semibold text-black mb-3">Allgemeine Hinweise zu den Rechtsgrundlagen der Datenverarbeitung</h3>
                  <p className="text-black/70">
                    Sofern Sie in die Datenverarbeitung eingewilligt haben, verarbeiten wir Ihre personenbezogenen 
                    Daten auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO bzw. Art. 9 Abs. 2 lit. a DSGVO, sofern 
                    besondere Datenkategorien nach Art. 9 Abs. 1 DSGVO verarbeitet werden. Im Falle einer 
                    ausdrücklichen Einwilligung in die Übertragung personenbezogener Daten in Drittstaaten erfolgt 
                    die Datenverarbeitung außerdem auf Grundlage von Art. 49 Abs. 1 lit. a DSGVO. Sofern Sie in 
                    die Speicherung von Cookies oder in den Zugriff auf Informationen in Ihr Endgerät (z. B. via 
                    Device-Fingerprinting) eingewilligt haben, erfolgt die Datenverarbeitung zusätzlich auf 
                    Grundlage von § 25 Abs. 1 TDDDG. Die Einwilligung ist jederzeit widerrufbar.
                  </p>
                </div>

                {/* Section 8: Data Subject Rights */}
                <div className="mb-10">
                  <h3 className="text-xl font-semibold text-black mb-3">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h3>
                  <p className="text-black/70 mb-4">
                    Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie 
                    können eine bereits erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis 
                    zum Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.
                  </p>
                  <h3 className="text-xl font-semibold text-black mb-3">Widerspruchsrecht gegen die Datenerhebung in besonderen Fällen</h3>
                  <p className="text-black/70 mb-4">
                    WENN DIE DATENVERARBEITUNG AUF GRUNDLAGE VON ART. 6 ABS. 1 LIT. E ODER F DSGVO ERFOLGT, HABEN 
                    SIE JEDERZEIT DAS RECHT, AUS GRÜNDEN, DIE SICH AUS IHRER BESONDEREN SITUATION ERGEBEN, GEGEN 
                    DIE VERARBEITUNG IHRER PERSONENBEZOGENEN DATEN WIDERSPRUCH EINZULEGEN. DIE JEWEILIGE 
                    RECHTSGRUNDLAGE, AUF DENEN EINE VERARBEITUNG BERUHT, ENTNEHMEN SIE DIESER DATENSCHUTZERKLÄRUNG.
                  </p>
                  <h3 className="text-xl font-semibold text-black mb-3">Beschwerderecht bei der zuständigen Aufsichtsbehörde</h3>
                  <p className="text-black/70">
                    Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei einer 
                    Aufsichtsbehörde, insbesondere in dem Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres 
                    Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes zu. Das Beschwerderecht besteht 
                    unbeschadet anderweitiger verwaltungsrechtlicher oder gerichtlicher Rechtsbehelfe.
                  </p>
                </div>

                {/* Section 9: Data Portability */}
                <div className="mb-10">
                  <h3 className="text-xl font-semibold text-black mb-3">Recht auf Datenübertragbarkeit</h3>
                  <p className="text-black/70">
                    Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines 
                    Vertrags automatisiert verarbeiten, an sich oder an einen Dritten in einem gängigen, 
                    maschinenlesbaren Format aushändigen zu lassen. Sofern Sie die direkte Übertragung der Daten 
                    an einen anderen Verantwortlichen verlangen, erfolgt dies nur, soweit es technisch machbar ist.
                  </p>
                </div>

                {/* Section 10: Access, Correction, Deletion */}
                <div className="mb-10">
                  <h3 className="text-xl font-semibold text-black mb-3">Auskunft, Berichtigung und Löschung</h3>
                  <p className="text-black/70">
                    Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf 
                    unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und 
                    Empfänger und den Zweck der Datenverarbeitung und ggf. ein Recht auf Berichtigung oder 
                    Löschung dieser Daten. Hierzu sowie zu weiteren Fragen zum Thema personenbezogene Daten 
                    können Sie sich jederzeit an uns wenden.
                  </p>
                </div>

                {/* Section 11: Data Collection on Website */}
                <div className="mb-10">
                  <h2 className="text-2xl font-bold text-black mb-4">4. Datenerfassung auf dieser Website</h2>
                  <h3 className="text-xl font-semibold text-black mb-3">Server-Log-Dateien</h3>
                  <p className="text-black/70 mb-4">
                    Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten 
                    Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:
                  </p>
                  <ul className="list-disc list-inside text-black/70 mb-4 space-y-1">
                    <li>Browsertyp und Browserversion</li>
                    <li>verwendetes Betriebssystem</li>
                    <li>Referrer URL</li>
                    <li>Hostname des zugreifenden Rechners</li>
                    <li>Uhrzeit der Serveranfrage</li>
                    <li>IP-Adresse</li>
                  </ul>
                  <p className="text-black/70">
                    Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die 
                    Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der 
                    Websitebetreiber hat ein berechtigtes Interesse an der technisch fehlerfreien Darstellung 
                    und der Optimierung seiner Website – hierzu müssen die Server-Log-Files erfasst werden.
                  </p>
                </div>

                {/* Section 12: Contact Form */}
                <div className="mb-10">
                  <h3 className="text-xl font-semibold text-black mb-3">Kontaktformular</h3>
                  <p className="text-black/70 mb-4">
                    Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem 
                    Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung 
                    der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben 
                    wir nicht ohne Ihre Einwilligung weiter.
                  </p>
                  <p className="text-black/70">
                    Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern 
                    Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung 
                    vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung 
                    auf unserem berechtigten Interesse an der effektiven Bearbeitung der an uns gerichteten 
                    Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a 
                    DSGVO) sofern diese abgefragt wurde.
                  </p>
                </div>

                {/* Section 13: Email Contact */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-black mb-3">Anfrage per E-Mail, Telefon oder Telefax</h3>
                  <p className="text-black/70">
                    Wenn Sie uns per E-Mail, Telefon oder Telefax kontaktieren, wird Ihre Anfrage inklusive 
                    aller daraus hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der 
                    Bearbeitung Ihres Anliegens bei uns gespeichert und verarbeitet. Diese Daten geben wir 
                    nicht ohne Ihre Einwilligung weiter. Die Verarbeitung dieser Daten erfolgt auf Grundlage 
                    von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags 
                    zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen 
                    übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven 
                    Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf Ihrer 
                    Einwilligung (Art. 6 Abs. 1 lit. a DSGVO) sofern diese abgefragt wurde.
                  </p>
                </div>

              </div>
            </div>

            {/* Last Updated */}
            <p className="text-center text-black/40 text-sm mt-8">
              Stand: Januar 2026
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DatenschutzPage;
